import React from 'react';
import { Bot } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { WorkspaceResultsHeader, WorkspaceStatusBadge } from '@/modules/app/components/workspace/WorkspaceIndicators';
import { EnhancedLoadingState } from '@/modules/app/components/ui/LoadingStates';
import { AdvancedTable } from '@/modules/app/components/ui/table';
import ExportButton from '@/components/ui/ExportButton';
import { isDebugMode } from '@/lib/debugUtils';
import { useAuth } from '@/contexts/AuthContext';

// Import new components and utilities
import { InlineNotification } from './components';
import { KeywordTabsProps } from './types/keywordTabsTypes';
import { TAB_CONFIG } from './config/tabConfig';
import { getColumns } from './utils/columnUtils';
import { cellRenderer } from './utils/cellRenderer';
import { useKeywordTabsLogic } from './hooks/useKeywordTabsLogic';

const KeywordTabs: React.FC<KeywordTabsProps> = (props) => {
  const { user } = useAuth();
  
  // Use the centralized business logic hook
  const {
    // State
    activeTab,
    notification,
    data,
    isLoading,
    error,
    connectionStatus,
    validatedUserId,
    availableTabs,
    currentTabData,
    
    // AI Analysis
    isAiAnalysisLoading,
    aiAnalysisError,
    getAiErrorMessage,
    clearAiError,
    
    // Handlers
    handleTabChange,
    handleAiAnalysis,
    setNotification,
  } = useKeywordTabsLogic(props);

  const columns = getColumns(activeTab);

  // Enhanced empty state with workspace context and user ID validation info
  if (!validatedUserId || !props.currentQuery) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        {!validatedUserId ? (
          <div>
            <p className="text-destructive mb-2">⚠️ Authentication Issue</p>
            <p className="text-sm">
              {!props.userId ? 'Please log in to start your research' : 'User ID validation failed - please refresh the page'}
            </p>
          </div>
        ) : (
          <p>Enter a keyword above to start your research</p>
        )}
        {props.workspaceId && (
          <div className="mt-2">
            <WorkspaceStatusBadge 
              workspaceName={props.workspaceName || `Workspace ${props.workspaceId.slice(-8)}`}
              className="justify-center"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Inline Notification Above Tabs with Workspace Context */}
      {notification && (
        <InlineNotification
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onDismiss={() => setNotification(null)}
        />
      )}

      {/* Enhanced Tab Navigation with Color Kit styling */}
      <div className="border-b border-border bg-muted/30">
        <div className="flex justify-between items-center p-4">
          <div className="flex space-x-1">
            {availableTabs.map((tab) => {
              const Icon = tab.icon;
              // Calculate count for this tab
              const count = tab.dataTypes 
                ? tab.dataTypes.reduce((total, dataType) => 
                    total + (data?.filter(item => item.data_type === dataType).length || 0), 0)
                : data?.filter(item => item.data_type === tab.dataType).length || 0;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs">
                      {count}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
            {/* AI Analysis Button - positioned on the right with enhanced visibility */}
          {data && data.length > 0 && (
            <Button
              onClick={handleAiAnalysis}
              disabled={isAiAnalysisLoading || !data || data.length === 0}
              size="default"
              className="flex items-center gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium px-6"
            >
              {isAiAnalysisLoading ? (
                <div className="relative">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <div className="absolute inset-0 rounded-full animate-pulse bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 opacity-30 blur-sm"></div>
                </div>
              ) : (
                <div className="relative">
                  <Bot className="h-4 w-4" />
                  {/* Beam effect - rotating border around icon only - ALWAYS VISIBLE */}
                  <div className="absolute -inset-0.5 rounded-full opacity-100">
                    <div className="absolute inset-0 rounded-full" style={{
                      background: 'conic-gradient(from 0deg, #10b981, #fbbf24, #ef4444, #10b981)',
                      animation: 'spin 2s linear infinite',
                      padding: '1px'
                    }}>
                      <div className="absolute inset-0 rounded-full bg-white"></div>
                    </div>
                  </div>
                </div>
              )}
              <span className="hidden sm:inline">
                {isAiAnalysisLoading ? 'Analyzing...' : 'AI Analysis'}
              </span>
            </Button>
          )}
        </div>
        
        {/* Enhanced workspace indicator with Color Kit styling - now clickable */}
        {props.workspaceId && (
          <div className="px-4 pb-2">
            <a 
              href={`/app/workspace/${props.workspaceId}`}
              className="inline-block"
            >
              <WorkspaceStatusBadge 
                workspaceName={props.workspaceName || `Workspace ${props.workspaceId.slice(-8)}`}
                isProcessing={isLoading}
                resultCount={data?.length}
                connectionStatus={connectionStatus}
                className="text-xs hover:bg-accent/40 transition-colors cursor-pointer"
              />
            </a>
          </div>
        )}
      </div>


      {/* Enhanced Content Area with Color Kit styling */}
      <div className="p-6">
        {/* Enhanced loading state with workspace context */}
        {isLoading && (
          <EnhancedLoadingState
            message={`Loading ${TAB_CONFIG.find(t => t.id === activeTab)?.label.toLowerCase()} data...`}
            description={
              props.workspaceId
                ? `Processing results for workspace "${props.workspaceName || props.workspaceId.slice(-8)}"...`
                : "This may take a moment. We'll also check for updates with smart polling intervals."
            }
            phase="database"
            className="bg-accent/10 border-accent/20"
          />
        )}

        {/* Enhanced error state with Color Kit destructive styling */}
        {!isLoading && error && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-destructive mb-2">⚠️ Error loading data</div>
              <div className="text-muted-foreground text-sm">{error.message}</div>
              {isDebugMode(user?.primaryEmailAddress?.emailAddress) && error.code && (
                <div className="text-xs text-muted-foreground mt-1">Code: {error.code}</div>
              )}
            </div>
          </div>
        )}

        {/* Enhanced no data state with workspace context */}
        {!isLoading && !error && (!data || data.length === 0) && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
              <div className="text-muted-foreground mb-2">Processing your keyword research...</div>
              <div className="text-muted-foreground text-sm">
                This may take a moment while we gather comprehensive data.
              </div>
              {props.workspaceId && (
                <div className="text-xs text-muted-foreground mt-2 opacity-70">
                  Results will be associated with your workspace
                </div>
              )}
            </div>
          </div>
        )}

        {/* Enhanced results table with workspace context header */}
        {!isLoading && data && data.length > 0 && (
          <div className="w-full">
            {/* Enhanced results header with workspace info */}
            <WorkspaceResultsHeader
              keyword={props.currentQuery}
              workspaceName={props.workspaceName}
              language={props.currentLanguageName}
              location={props.currentCountryName}
              resultCount={currentTabData.length}
              dataType={TAB_CONFIG.find(t => t.id === activeTab)?.label.toLowerCase() || 'keyword'}
              connectionStatus={connectionStatus}
              className="mb-6"
            />
            
            {/* Enhanced AdvancedTable with Notion-style filtering and sorting */}
            <AdvancedTable
              data={currentTabData}
              columns={columns}
              title={`${TAB_CONFIG.find(t => t.id === activeTab)?.label} Keywords`}
              subtitle={props.workspaceName ? `Workspace: ${props.workspaceName}` : undefined}
              cellRenderer={(value, column, row) => cellRenderer(value, column, row, user?.primaryEmailAddress?.emailAddress)}
              isLoading={isLoading}
              enableFiltering={true}
              enableSorting={true}
              enableGlobalSearch={true}
              enableColumnVisibility={true}
              enableExport={true}
              onExport={(data) => {
                // Convert data for export button
                const exportData = data.map(row => {
                  const exportRow: Record<string, any> = {};
                  columns.forEach(col => {
                    exportRow[col.key] = row[col.key];
                  });
                  return exportRow;
                });
                // You could trigger download here or use ExportButton component
              }}
              toolbarActions={
                <ExportButton
                  data={currentTabData}
                  columns={columns}
                  filename={`simple_keyword_${activeTab}`}
                  workspaceName={props.workspaceName}
                  tabName={activeTab}
                />
              }
              tableStateConfig={{
                persistenceKey: `simple-keywords-${activeTab}`,
                persistToLocalStorage: true,
                pageSize: 20
              }}
              className="w-full"
            />
          </div>
        )}

        {/* Show AI Analysis Error if present */}
        {aiAnalysisError && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <div className="flex items-center gap-2 text-destructive text-sm">
              <div className="h-4 w-4" />
              <span>{getAiErrorMessage()}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAiError}
                className="ml-auto h-6 w-6 p-0"
              >
                ×
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default KeywordTabs;